import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    Typography,
    Textarea,
    Button,
    IconButton,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Spinner
} from "@material-tailwind/react";
import {
    EllipsisVerticalIcon,
    PencilIcon,
    TrashIcon
} from "@heroicons/react/24/outline";
import ConfirmModal from '../../common/ConfirmModal';
const {
    extractActiveMention,
    insertMention,
    splitCommentMentions
} = require('./commentMentions');

const TaskCommentSection = ({ taskId }) => {
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');

    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [editingComment, setEditingComment] = useState(null);
    const [editText, setEditText] = useState('');
    const [deleteCommentId, setDeleteCommentId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [newCommentMentionQuery, setNewCommentMentionQuery] = useState('');
    const [newCommentMentionRange, setNewCommentMentionRange] = useState(null);
    const [newCommentMentionsOpen, setNewCommentMentionsOpen] = useState(false);
    const [newCommentMentions, setNewCommentMentions] = useState([]);
    const [newCommentMentionsLoading, setNewCommentMentionsLoading] = useState(false);

    useEffect(() => {
        let isActive = true;

        const fetchComments = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${rootUrl}/api/tasks/${taskId}/comments`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (isActive && response.data.success) {
                    setComments(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching comments:', error);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        fetchComments();

        return () => {
            isActive = false;
        };
    }, [rootUrl, taskId, token]);

    useEffect(() => {
        if (!newCommentMentionsOpen) {
            setNewCommentMentions([]);
            setNewCommentMentionsLoading(false);
            return;
        }

        let isActive = true;
        const timeout = setTimeout(async () => {
            try {
                setNewCommentMentionsLoading(true);
                const response = await axios.get(
                    `${rootUrl}/api/projects/users`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params: {
                            search: newCommentMentionQuery || ''
                        }
                    }
                );

                if (isActive && response.data.success) {
                    setNewCommentMentions((response.data.data || []).slice(0, 8));
                }
            } catch (error) {
                if (isActive) {
                    console.error('Error fetching mention suggestions:', error);
                    setNewCommentMentions([]);
                }
            } finally {
                if (isActive) {
                    setNewCommentMentionsLoading(false);
                }
            }
        }, 200);

        return () => {
            isActive = false;
            clearTimeout(timeout);
        };
    }, [newCommentMentionsOpen, newCommentMentionQuery, rootUrl, token]);

    const handleAddComment = async () => {
        if (!newComment.trim()) return;

        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/${taskId}/comments`,
                {
                    comment: newComment
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setComments([response.data.data, ...comments]);
                setNewComment('');
                setNewCommentMentionsOpen(false);
                setNewCommentMentions([]);
                setNewCommentMentionQuery('');
                setNewCommentMentionRange(null);
            }
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const handleEditComment = async (commentId) => {
        try {
            const response = await axios.put(
                `${rootUrl}/api/tasks/comments/${commentId}`,
                {
                    comment: editText
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setComments(comments.map(c =>
                    c.comment_id === commentId ? response.data.data : c
                ));
                setEditingComment(null);
                setEditText('');
            }
        } catch (error) {
            console.error('Error updating comment:', error);
        }
    };

    const handleDeleteComment = async () => {
        if (!deleteCommentId) return;

        setDeleteLoading(true);
        setDeleteError('');

        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/comments/${deleteCommentId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setComments(comments.filter(c => c.comment_id !== deleteCommentId));
                setDeleteCommentId(null);
            } else {
                setDeleteError(response.data.message || 'Failed to delete comment');
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
            setDeleteError(error?.response?.data?.message || 'Failed to delete comment');
        } finally {
            setDeleteLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getUserInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const handleNewCommentChange = (event) => {
        const value = event.target.value;
        setNewComment(value);

        const activeMention = extractActiveMention(value, event.target.selectionStart ?? value.length);
        if (activeMention.active) {
            setNewCommentMentionQuery(activeMention.query || '');
            setNewCommentMentionRange({ start: activeMention.start, end: activeMention.end });
            setNewCommentMentionsOpen(true);
        } else {
            setNewCommentMentionsOpen(false);
            setNewCommentMentionRange(null);
            setNewCommentMentionQuery('');
        }
    };

    const handleSelectNewCommentMention = (username) => {
        if (!newCommentMentionRange) return;

        const insertion = insertMention(newComment, newCommentMentionRange, username);
        setNewComment(insertion.text);
        setNewCommentMentionsOpen(false);
        setNewCommentMentions([]);
        setNewCommentMentionQuery('');
        setNewCommentMentionRange(null);

        window.setTimeout(() => {
            const textarea = document.getElementById('task-comment-new');
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(insertion.caret, insertion.caret);
            }
        }, 0);
    };

    const renderCommentBody = (comment) => {
        const text = comment.comment || comment.comment_text || '';
        const parts = splitCommentMentions(text, comment.mentions || []);

        return (
            <Typography variant="small" className="text-gray-700 whitespace-pre-wrap break-words">
                {parts.map((part, idx) => {
                    if (part.type === 'mention') {
                        return (
                            <Link
                                key={`${part.username}-${idx}`}
                                to={`/users/${encodeURIComponent(part.username)}`}
                                className="font-semibold text-[#FF5733] hover:underline"
                            >
                                {part.text}
                            </Link>
                        );
                    }

                    return <span key={idx}>{part.text}</span>;
                })}
            </Typography>
        );
    };

    return (
        <div className="space-y-4">
            {/* New Comment Input */}
            <div className="space-y-2 relative">
                <div className="relative">
                    <Textarea
                        id="task-comment-new"
                        label="Add a comment"
                        value={newComment}
                        onChange={handleNewCommentChange}
                        rows={3}
                        placeholder="Type @ to mention staff..."
                    />
                    {newCommentMentionsOpen && (
                        <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-xl border border-blue-gray-100 bg-white shadow-lg">
                            <div className="flex items-center justify-between border-b border-blue-gray-50 px-3 py-2">
                                <Typography variant="small" className="font-semibold text-blue-gray-700">
                                    Mention staff
                                </Typography>
                                <Typography variant="small" color="gray">
                                    @{newCommentMentionQuery || 'all'}
                                </Typography>
                            </div>
                            <div className="max-h-60 overflow-y-auto">
                                {newCommentMentionsLoading ? (
                                    <div className="flex items-center justify-center py-4">
                                        <Spinner className="h-5 w-5" color="blue" />
                                    </div>
                                ) : newCommentMentions.length === 0 ? (
                                    <div className="px-3 py-3 text-sm text-gray-500">
                                        No staff matches this mention.
                                    </div>
                                ) : (
                                    newCommentMentions.map((user) => (
                                        <button
                                            key={user.id}
                                            type="button"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => handleSelectNewCommentMention(user.username)}
                                            className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-blue-50"
                                        >
                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FF5733] text-[11px] font-semibold text-white">
                                                {getUserInitials(user.name || user.username)}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-semibold text-blue-gray-800">
                                                    {user.name || user.username}
                                                </div>
                                                <div className="truncate text-xs text-gray-500">
                                                    @{user.username}
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex justify-end">
                    <Button
                        size="sm"
                        className="bg-[#FF5733]"
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                    >
                        Add Comment
                    </Button>
                </div>
            </div>

            {/* Comments List */}
            {loading ? (
                <div className="flex justify-center py-8">
                    <Spinner className="h-8 w-8" color="blue" />
                </div>
            ) : comments.length === 0 ? (
                <div className="text-center py-8">
                    <Typography color="gray">No comments yet. Be the first to comment!</Typography>
                </div>
            ) : (
                <div className="space-y-4">
                    {comments.map((comment) => (
                        <div key={comment.comment_id} className="flex gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FF5733] text-xs font-semibold text-white">
                                {getUserInitials(comment.created_by_name || comment.created_by_username)}
                            </div>

                            <div className="flex-1">
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <Typography variant="small" className="font-semibold">
                                            {comment.created_by_name || comment.created_by_username || 'Unknown User'}
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Typography variant="small" color="gray">
                                                {formatDate(comment.created_at)}
                                            </Typography>
                                            <Menu>
                                                <MenuHandler>
                                                    <IconButton variant="text" size="sm">
                                                        <EllipsisVerticalIcon className="h-4 w-4" />
                                                    </IconButton>
                                                </MenuHandler>
                                                <MenuList>
                                                    <MenuItem
                                                        className="flex items-center gap-2"
                                                        onClick={() => {
                                                            setEditingComment(comment.comment_id);
                                                            setEditText(comment.comment || comment.comment_text || '');
                                                        }}
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                        Edit
                                                    </MenuItem>
                                                    <MenuItem
                                                        className="flex items-center gap-2 text-red-500"
                                                        onClick={() => setDeleteCommentId(comment.comment_id)}
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                        Delete
                                                    </MenuItem>
                                                </MenuList>
                                            </Menu>
                                        </div>
                                    </div>

                                    {editingComment === comment.comment_id ? (
                                        <div className="space-y-2 mt-2">
                                            <Textarea
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                rows={3}
                                            />
                                            <div className="flex gap-2 justify-end">
                                                <Button
                                                    size="sm"
                                                    variant="text"
                                                    onClick={() => {
                                                        setEditingComment(null);
                                                        setEditText('');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    className="bg-[#FF5733]"
                                                    onClick={() => handleEditComment(comment.comment_id)}
                                                >
                                                    Save
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        renderCommentBody(comment)
                                    )}

                                    {comment.updated_at && comment.updated_at !== comment.created_at && (
                                        <Typography variant="small" color="gray" className="mt-1 italic">
                                            (edited)
                                        </Typography>
                                    )}
                                </div>

                            </div>
                        </div>
                    ))}
                </div>
            )}
            <ConfirmModal
                isOpen={Boolean(deleteCommentId)}
                title="Delete comment"
                description="This comment will be removed from the task."
                onCancel={() => {
                    setDeleteCommentId(null);
                    setDeleteError('');
                }}
                onConfirm={handleDeleteComment}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />
        </div>
    );
};

export default TaskCommentSection;
